import React, { useContext, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { withNamespaces } from 'react-i18next';
import DOMPurify from 'dompurify';
import { Chat } from "@progress/kendo-react-conversational-ui";
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import LoadingOverlay from 'react-loading-overlay';
import BeatLoader from 'react-spinners/BeatLoader';
import i18next from 'i18next';
import "@progress/kendo-theme-default/dist/all.css";
import * as signalR from '@microsoft/signalr';
import { ChatId, Company, UserAssociation } from '@users';
import { loadPreviousChatHistory, getInitials, savePrivateMessage, updateReadDatePrivateMessage } from '@actions/chat';
import * as storage from '@utils/storage'
import { ChatContext } from '@/ChatProvider';
import { actionTypes } from '@utils/constants';
import { formatTimestamp } from '@utils/index';
import { GetLanguage } from '@utils/language';
import * as StatusCode from '@scripts/StatusCode';
import { isIOS } from '../../utils';

const ChatDisplay = (props) => {

    const { data } = props;
    const chatRef = useRef(null);
    const { state, dispatch } = useContext(ChatContext);
    const [messages, setMessages] = useState([]);
    const [isiOS, setIsiOS] = React.useState(isIOS());
    const [loading, setLoading] = React.useState(false);
    const [locale, setLocale] = React.useState(GetLanguage());
    const [orientation, setOrientation] = useState(window.screen.orientation.type);

    useLayoutEffect(() => {
        resizeForm();
        setTimeout(function () {
            const container = document.querySelector('#divDetail');
            container.scrollTop = container.scrollHeight;
        }, 0);
    }, []);

    useEffect(() => {

        const handleOrientationChange = () => {
            setOrientation(window.screen.orientation.type);
        };

        window.screen.orientation.addEventListener('change', handleOrientationChange);
        chatRef.current.viewItemsWrapperEl.addEventListener('scroll', handleScroll);

        if (data.unReadMessagesCounter > 0) {
            // Update read date
            setLoading(true);
            updateReadDatePrivateMessage(data.konekUserAssociationId, UserAssociation()).then(async (response) => {
                if (response == undefined) {
                    dispatch({ type: actionTypes.GENERICERROR, payload: 'No response from server' });
                    setLoading(false);
                    return;
                } else if (response.status === true) {
                    dispatch({ type: actionTypes.READ_MESSAGE, payload: recipient.id });
                    setLoading(false);
                }
            })
                .catch(error => {
                    dispatch({ type: actionTypes.GENERICERROR, payload: error });
                    setLoading(false);
                });
        } else {
            dispatch({ type: actionTypes.READ_MESSAGE, payload: recipient.id });
        }

        // Cleanup on unmount
        return () => {
            window.screen.orientation.removeEventListener('change', handleOrientationChange);
            chatRef.current.viewItemsWrapperEl.removeEventListener('scroll', handleScroll);
            dispatch({ type: actionTypes.UNREAD_MESSAGE });            
        };
    }, []);

    useEffect(() => {
        setMessages(state.messages.filter(x => (x.author.id == recipient.id) || (x.recipient.id == recipient.id)));
        chatRef.current.viewItemsWrapperEl.addEventListener('scroll', handleScroll);

        // Cleanup on unmount
        return () => {
            chatRef.current.viewItemsWrapperEl.removeEventListener('scroll', handleScroll);
        };
    }, [state.messages]);

    useEffect(() => {
        setTimeout(function () {
            const container = document.querySelector('.k-message-list.k-avatars');
            container.scrollTop = container.scrollHeight;
        }, 0);
    }, [messages]);

    useEffect(() => {
        resizeForm();
        setTimeout(function () {
            const divDetail = document.querySelector('#divDetail');
            divDetail.scrollTop = divDetail.scrollHeight;
            const container = document.querySelector('.k-message-list.k-avatars');
            container.scrollTop = container.scrollHeight;
        }, 0);
    }, [orientation]);

    const sender = {
        id: ChatId(),
        name: storage.getUserName(),
        initials: getInitials(storage.getUserName())
    };

    const recipient = {
        id: data.userCode + data.konekUserAssociationId + Company(),
        name: data.fullName,
        initials: getInitials(data.fullName)
    };

    const avatarTemplate = (props) => {
        const isAuthor = props.item.author.id === ChatId();
        const containerStyle = {
            display: 'flex',
            flexDirection: isAuthor ? 'row-reverse' : 'row', // Switch position
            alignItems: 'center',
            gap: '8px',
        };

        const avatarStyle = {
            position: 'relative',
            width: '31px',
            minWidth: '31px',
            height: '31px',
            borderRadius: '50%',
            backgroundColor: 'var(--color1)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: '12px',
            wordBreak: 'break-word'
        };


        const statusCircleStyle = {
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: data.onLine ? '#44b700' : '#B0B3B8',
            border: '1px solid white'
        };

        return (
            <div style={containerStyle}>
                {props.item.author.id !== ChatId() && <div style={avatarStyle}>
                    {props.item.author.initials}
                    <span style={statusCircleStyle}></span>
                </div>}
                <div className="k-chat-bubble" style={{ maxWidth: '270px', wordBreak: 'break-word', order: '0', backgroundColor: isAuthor ? 'var(--color1)' : '', borderColor: isAuthor ? 'var(--color1)' : '' }}>
                    {props.item.text}
                </div>
            </div>
        );
    };

    const messageTemplate = (props) => (
        <div style={{
            display: 'flex',
            justifyContent: props.item.author.id === ChatId() ? 'flex-end' : 'flex-start',
        }}>
            {avatarTemplate(props)}
        </div>
    );

    const resizeForm = () => {
        setTimeout(function () {
            const height = window.innerHeight;
            const headerElement = document.querySelector('[data-id=headerChatDetail]');
            
            if (headerElement != null) {

                const header = headerElement.offsetHeight;
                const offSet = isiOS ? 72 : 55;
                const customHeight = height - header - offSet;
                const element = document.querySelector('#divDetail');

                if (element != null) {
                    element.style.height = `${customHeight}px`;
                    element.style.overflowX = 'hidden';
                }
            }
        }, 0);
    }

    const sendMessage = (event) => {
        if (state.connection && state.connection.state === signalR.HubConnectionState.Connected) {
            // Save message before send            
            const purifiedMessage = DOMPurify.sanitize(event.message.text);

            if (purifiedMessage?.trim()) {
                setLoading(true);
                savePrivateMessage(UserAssociation(), data.konekUserAssociationId, purifiedMessage).then(async (response) => {
                    if (response == undefined) {
                        dispatch({ type: actionTypes.GENERICERROR, payload: 'No response from server' });
                        setLoading(false);
                        return;
                    }

                    if (response.status === false && response.errorMsg.length > 0) {
                        dispatch({ type: actionTypes.CUSTOMERROR, payload: response.errorMsg });
                        setLoading(false);
                    }
                    else if (response.status === true) {
                        try {
                            event.message.id = response.chatId;
                            event.message.recipient = recipient;
                            event.message.readDate = null;
                            event.message.author.name = formatTimestamp(event.message.timestamp);

                            dispatch({ type: actionTypes.SEND_MESSAGE, payload: event.message });
                            await state.connection.invoke("SendPrivateMessage", recipient.id, purifiedMessage, response.chatId);
                            setLoading(false);
                        } catch (error) {
                            dispatch({ type: actionTypes.GENERICERROR, payload: error });
                            setLoading(false);
                        }
                    }
                })
                    .catch(error => {
                        dispatch({ type: actionTypes.GENERICERROR, payload: error });
                        setLoading(false);
                    });
            } else {
                //dispatch({ type: actionTypes.GENERICERROR, payload: 'No typed message' });
            }
        } else {
            dispatch({ type: actionTypes.GENERICERROR, payload: 'No connection to server yet.' });
        }
    };

    const handleScroll = async (e) => {

        const scrollableElement = chatRef.current.chatWrapperEl.querySelector('.k-message-list');
        
        if (scrollableElement.scrollTop === 0) {

            const msgs = state.messages.filter(x => (x.author.id == recipient.id) || (x.recipient.id == recipient.id));

            if (msgs.filter(x => x.isFirstChat).length === 0) {

                setLoading(true);
                const minChatId = Math.min(...chatRef.current.props.messages.map(message => message.id));

                loadPreviousChatHistory(UserAssociation(), data.konekUserAssociationId, minChatId).then(async (response) => {

                    if (response == undefined) {
                        setLoading(false);
                        dispatch({ type: actionTypes.GENERICERROR, payload: 'No response from server.' });
                        return;
                    } else if (response?.status == StatusCode.STATUS_OK) {
                        dispatch({
                            type: actionTypes.LOAD_PREV_CHAT, payload: response.data.map(chat => ({
                                id: chat.chatId,
                                author: {
                                    id: chat.senderUserCode + chat.senderId + Company(),
                                    name: chat.senderFullName,
                                    initials: getInitials(chat.senderFullName)
                                },
                                recipient: {
                                    id: chat.recipientUserCode + chat.recipientId + Company(),
                                    initials: getInitials(chat.recipientFullName)
                                },
                                timestamp: chat.dispachDate !== null ? new Date(chat.dispachDate.toLocaleString()) : chat.dispachDate,
                                text: chat.message,
                                readDate: chat.readDate !== null ? new Date(chat.readDate.toLocaleString()) : chat.readDate,
                                isFirstChat: chat.isFirstChat,
                            }))
                        });
                        setLoading(false);
                    }
                })
                    .catch(error => {
                        setLoading(false);
                        dispatch({ type: actionTypes.GENERICERROR, payload: error });
                    });
            }
        }
    };

    return (
        <LoadingOverlay
            active={loading}
            spinner={<BeatLoader />}
            text={i18next.t('chat:display.loading')}
        >
            <div id='divDetail'>
                <IntlProvider locale={locale}>
                    <LocalizationProvider language={locale}>
                        <Chat ref={chatRef}
                            user={sender}
                            messages={messages}
                            messageTemplate={messageTemplate}
                            onMessageSend={sendMessage}                            
                            placeholder={i18next.t('chat:display.placeholderTypeMessage')}
                        />
                    </LocalizationProvider>
                </IntlProvider>
            </div>
        </LoadingOverlay>
    );
};

export default withNamespaces(['chat', 'error'], { wait: true })(ChatDisplay);